import {
  type DoctorServiceExpanded,
  useV1AppointmentCreate,
  type UseV1AppointmentCreateParams,
  useV1AppointmentValidate,
  type UseV1AppointmentValidateParams,
} from "@hermes/api";
import type { GenericErrorResponse } from "@hermes/api/src/errors";
import { useFlag } from "@hermes/flags";
import { toast } from "@hermes/shared";
import { type FormErrorsContainer, useScreenDetector } from "@hermes/ui";
import { capitalize, hashSHA256 } from "@hermes/utils";
import { captureException } from "@sentry/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCookiebot, useGtag, useVWO, useYm } from "#internal/app/analytics";
import { useAppConfig } from "#internal/app/config";
import { useRoutePath } from "#internal/app/router";

import type { AppointmentModalProps } from "..";
import type { AppointmentAdditionalFields } from "../additional-fields";
import type { AppointmentModalStep } from "../async-content";
import { AppointmentCreateError, AppointmentValidateError } from "../errors";
import type {
  AppointmentFormFields,
  AppointmentInsuranceFields,
  AppointmentOtpFields,
} from "./use-fields";

export const useSubmitAppointmentForm = ({
  selectedService,
  fields,
  additionalFields,
  insuranceFields,
  otpFields,
  errors,
  extractAdditionalField,
  request_meta,
  step,
  onChangeStep,
  city,
  onCompleted,
  onSendOtp,
}: {
  selectedService?: DoctorServiceExpanded;
  fields: AppointmentFormFields;
  additionalFields: AppointmentAdditionalFields;
  insuranceFields: AppointmentInsuranceFields;
  otpFields: AppointmentOtpFields;
  errors: FormErrorsContainer;
  extractAdditionalField: (param_name: string) => any;
  step: AppointmentModalStep;
  onChangeStep: (step: AppointmentModalStep) => void;
  onSendOtp?: (phone: string) => Promise<boolean | undefined>;
} & Pick<AppointmentModalProps, "request_meta" | "city" | "onCompleted">) => {
  const { isMobile } = useScreenDetector();
  const { country } = useAppConfig();
  const { routePath } = useRoutePath();
  const navigate = useNavigate();
  const flag = useFlag();
  const gtag = useGtag();
  const ym = useYm();
  const vwo = useVWO();
  const cookiebot = useCookiebot();

  const { mutateAsync: mutateValidate } = useV1AppointmentValidate();
  const { mutateAsync: mutateCreate, isPending: isCreatePending } =
    useV1AppointmentCreate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateAppointment = async (
    payload: UseV1AppointmentValidateParams,
  ): Promise<boolean> => {
    const response = await mutateValidate(payload);
    if (response.ok === false) {
      handleValidationError(response.error, payload);
      return false;
    }
    return true;
  };

  const createAppointment = async (
    payload: UseV1AppointmentCreateParams,
  ): Promise<{ id: number; hash: string } | null> => {
    const response = await mutateCreate(payload);
    if (response.ok === false) {
      handleCreateError(response.error, payload);
      return null;
    }
    return response.data;
  };

  const handleValidationError = (
    error: GenericErrorResponse,
    payload: UseV1AppointmentValidateParams,
  ): void => {
    captureException(
      new AppointmentValidateError(error.errors[0]?.detail || "Unknown error"),
      {
        level: "error",
        tags: { booking: "validate" },
        contexts: {
          request_data: payload,
          response_data: {
            type: error.type,
            errors: error.errors.map((err) => ({
              detail: err.detail,
              code: err.code,
            })),
          },
        },
        extra: {
          messages: error.errors.map((err) => err.detail),
          full_error_response: JSON.stringify(error),
        },
      },
    );

    if (error.type === "validation_error") {
      errors.update(
        error.errors.map((err) => ({
          key: err.attr ?? "generic",
          message: err.detail,
        })),
      );
    } else {
      error.errors.map(({ detail }) => toast.error(detail));
    }
  };

  const handleCreateError = (
    error: GenericErrorResponse,
    payload: UseV1AppointmentCreateParams,
  ): void => {
    captureException(
      new AppointmentCreateError(error.errors[0]?.detail || "Unknown error"),
      {
        level: "error",
        tags: { booking: "create" },
        contexts: {
          request_data: payload,
          response_data: {
            type: error.type,
            errors: error.errors.map((err) => ({
              detail: err.detail,
              code: err.code,
            })),
          },
        },
        extra: {
          messages: error.errors.map((err) => err.detail),
          full_error_response: JSON.stringify(error),
        },
      },
    );

    if (error.type === "validation_error") {
      errors.update(
        error.errors.map((err) => ({
          key: err.attr === "code.code" ? "otp" : (err.attr ?? "generic"),
          message: err.detail,
        })),
      );
    } else {
      error.errors.map(({ detail }) => toast.error(detail));
    }
  };

  const submitAppointmentForm = async (): Promise<void> => {
    if (!selectedService || !fields.slot.value) return;

    setIsSubmitting(true);
    try {
      const first_name = fields.name.value;
      const last_name = fields.surname.value || undefined;
      const [gaClientInfo, ymClientInfo] = await Promise.all([
        gtag.parseSessionData(),
        ym.parseSessionData(),
      ]).catch(() => []);

      const payload: UseV1AppointmentValidateParams = {
        client_name: first_name + (last_name ? " " + last_name : ""),
        client_phone: fields.phone.value,
        doctor_service: selectedService.id,
        slot: fields.slot.value.slot.id,
        source: isMobile ? "web-mobile" : "web-desktop",
        booking_page: request_meta?.booking_page,
        page_location: document.location.toString(),
        page_referrer: document.referrer,
        list_position: request_meta?.list_position,
        consents: cookiebot.getConsents(),
        additional_fields: Object.entries(additionalFields)
          .map(([key, field]) => {
            if (typeof field === "function") return null;
            return {
              clinic_branch_field: parseInt(key),
              value: String(field.value),
            };
          })
          .truthy(),
        ...(insuranceFields.checkbox.value && {
          insurance: insuranceFields.select.value?.id,
        }),
        ...(gaClientInfo && {
          google_client_id: gaClientInfo.client_id,
          google_session_id: gaClientInfo.session_id,
          google_session_number: gaClientInfo.session_number,
        }),
        ...(ymClientInfo && {
          analytics: [
            {
              client_id: ymClientInfo.client_id,
              type: 2,
            },
          ],
        }),
      };

      if (step === "form" && !(await validateAppointment(payload))) {
        return;
      }

      if (onSendOtp && step === "form") {
        const otpResponse = await onSendOtp(fields.phone.value);
        if (otpResponse) {
          onChangeStep("otp");
          return;
        }
      }
      gtag.sendEvent({ event: "submit-appointment-form-validated" });
      onCompleted?.();
      const appointment = await createAppointment({
        ...payload,
        ...(otpFields.code.value ? { code: otpFields.code.value } : {}),
      });

      if (!appointment) return;

      try {
        gtag.sendEvent({ event: "submit-appointment-form-success" });
        vwo.sendEvent("200SuccessfulAppointment");

        if (
          cookiebot.hasConsent("marketing") &&
          flag({ id: "appointment.enhanced-conversions" })
        ) {
          const normalize = (str?: string) => str?.toLowerCase().trim() ?? "";
          // FIXME: figure out same param name for every country
          const param_full_name: string | undefined =
            extractAdditionalField("full_name");
          const param_email: string | undefined =
            extractAdditionalField("Email") ??
            extractAdditionalField("patientEmail") ??
            extractAdditionalField("patient_email");
          const param_last_name: string | undefined =
            extractAdditionalField("LastName") ??
            extractAdditionalField("last_name") ??
            extractAdditionalField("patient_lastName");
          const param_postal_code: string | undefined =
            extractAdditionalField("billingZip");

          const [last, first] = (() => {
            // Surname env enabled, first and last name were required
            if (last_name) {
              return [last_name, first_name];
            }
            // Fullname was possibly written in a single field
            const [possible_last, possible_first] = first_name.split(" ");
            // If both are present, set them
            if (possible_last && possible_first) {
              return [possible_last, possible_first];
            }
            // Try param last name + first name from single name field
            if (param_last_name) {
              return [param_last_name, first_name];
            }
            // Try parsing from full name additional field
            const [last_full, first_full] = param_full_name?.split(" ") ?? [];
            if (last_full) {
              return [last_full, first_full ?? first_name];
            }
            // Fallback to just first name
            return [undefined, first_name];
          })();

          const sha256_phone_number = await hashSHA256(
            normalize(payload.client_phone.replace(/[^\d+]/g, "")),
          );
          const sha256_email_address = param_email
            ? {
                sha256_email_address: await hashSHA256(normalize(param_email)),
              }
            : {};
          const sha256_first_name = first_name
            ? { sha256_first_name: await hashSHA256(normalize(first)) }
            : {};
          const sha256_last_name = last_name
            ? { sha256_last_name: await hashSHA256(normalize(last)) }
            : {};
          const hashed_user_data = {
            sha256_phone_number,
            ...sha256_email_address,
            address: {
              country: country.countryId.toUpperCase(),
              ...sha256_first_name,
              ...sha256_last_name,
              ...(city ? { city: capitalize(normalize(city.name)) } : {}),
              ...(param_postal_code ? { postal_code: param_postal_code } : {}),
            },
          };
          gtag.setParameter("user_data", hashed_user_data);

          gtag.sendEvent({
            event: "form_submit",
            user_data: hashed_user_data,
          });
        }
      } catch (error) {
        captureException(error, {
          level: "error",
          tags: { booking: "google_analytics" },
          extra: {
            payload,
          },
        });
      } finally {
        navigate(
          routePath({
            path: "/appointments/:appointment_id",
            params: { appointment_id: appointment.id.toString() },
            query: { hash: appointment.hash },
          }),
        );
      }
    } catch (error) {
      captureException(error, {
        level: "error",
        tags: { booking: "general" },
        extra: {
          error: JSON.stringify(error),
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitAppointmentForm,
    isSubmitting,
    isCreatePending,
  };
};
